import { useEffect, useState, type FormEvent } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { api, ApiError } from '@forbiz/web-kit'
import {
  AuthErrorCode,
  passwordResetConfirmSchema,
  type PasswordResetInfoResponse,
} from '@forbiz/contracts'

import styles from './InviteAcceptPage.module.css'

/**
 * 비밀번호 재설정 화면(공개 경로 /reset-password?token=, BIZ-55).
 *
 * 링크의 토큰 유효성을 먼저 확인하고, 유효하면 새 비밀번호를 설정한다.
 * 성공하면 기존 세션은 서버가 모두 폐기하므로, 새 비밀번호로 다시 로그인한다.
 */
type LoadState =
  | { status: 'loading' }
  | { status: 'ready'; maskedEmail: string | null }
  | { status: 'done' }
  | { status: 'invalid'; message: string }

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') ?? ''

  const [load, setLoad] = useState<LoadState>({ status: 'loading' })
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [formError, setFormError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!token) {
      setLoad({ status: 'invalid', message: '재설정 링크에 토큰이 없습니다.' })
      return
    }
    let alive = true
    api
      .get<PasswordResetInfoResponse>(`/api/auth/password-reset?token=${encodeURIComponent(token)}`)
      .then((r) => {
        if (alive) {
          setLoad(
            r.valid
              ? { status: 'ready', maskedEmail: r.maskedEmail ?? null }
              : { status: 'invalid', message: '만료되었거나 유효하지 않은 링크입니다.' },
          )
        }
      })
      .catch(() => {
        if (alive) setLoad({ status: 'invalid', message: '만료되었거나 유효하지 않은 링크입니다.' })
      })
    return () => {
      alive = false
    }
  }, [token])

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (submitting) return
    setFieldErrors({})
    setFormError('')

    const parsed = passwordResetConfirmSchema.safeParse({ token, password })
    const errs: Record<string, string> = {}
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0])
        if (key && !errs[key]) errs[key] = issue.message
      }
    }
    if (password !== confirm) errs['confirm'] = '비밀번호가 일치하지 않습니다'
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs)
      return
    }

    setSubmitting(true)
    try {
      await api.post('/api/auth/password-reset/confirm', { token, password })
      setLoad({ status: 'done' })
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === AuthErrorCode.VALIDATION_FAILED && err.fields?.['password']) {
          setFieldErrors({ password: err.fields['password'] })
        } else {
          setFormError(err.message)
        }
        if (err.code === AuthErrorCode.RESET_EXPIRED || err.code === AuthErrorCode.RESET_INVALID) {
          setLoad({ status: 'invalid', message: err.message })
        }
      } else {
        setFormError('일시적인 오류가 발생했습니다. 잠시 후 다시 시도하세요')
      }
      setSubmitting(false)
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <h1 className={styles.brand}>포비즈</h1>
        <p className={styles.subtitle}>비밀번호 재설정</p>

        {load.status === 'loading' && (
          <p className={styles.status} role="status">
            링크를 확인하는 중…
          </p>
        )}

        {load.status === 'invalid' && (
          <div role="alert">
            <p className={styles.errorTitle}>링크를 사용할 수 없습니다</p>
            <p className={styles.errorBody}>{load.message}</p>
            <div className={styles.footer}>
              <Link to="/forgot-password">재설정 다시 요청</Link>
            </div>
          </div>
        )}

        {load.status === 'done' && (
          <div role="status">
            <p className={styles.status}>비밀번호가 변경되었습니다. 새 비밀번호로 로그인하세요.</p>
            <div className={styles.footer}>
              <Link to="/login">로그인 화면으로</Link>
            </div>
          </div>
        )}

        {load.status === 'ready' && (
          <form onSubmit={onSubmit} noValidate>
            {/* 어느 계정의 비밀번호를 바꾸는지 안심시킨다(마스킹 — 전체 이메일 비노출). */}
            {load.maskedEmail && (
              <p className={styles.hint} role="status">
                <b>{load.maskedEmail}</b> 계정의 비밀번호를 다시 설정합니다.
              </p>
            )}
            {formError && (
              <div className={styles.formError} role="alert">
                {formError}
              </div>
            )}

            <div className={styles.field}>
              <label className={styles.label} htmlFor="password">
                새 비밀번호
              </label>
              <input
                id="password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                aria-invalid={fieldErrors['password'] ? true : undefined}
                disabled={submitting}
              />
              {fieldErrors['password'] ? (
                <p className={styles.fieldError}>{fieldErrors['password']}</p>
              ) : (
                <p className={styles.hint}>10자 이상, 영문·숫자·기호 중 2종 이상</p>
              )}
            </div>

            <div className={styles.field}>
              <label className={styles.label} htmlFor="confirm">
                새 비밀번호 확인
              </label>
              <input
                id="confirm"
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                aria-invalid={fieldErrors['confirm'] ? true : undefined}
                disabled={submitting}
              />
              {fieldErrors['confirm'] && <p className={styles.fieldError}>{fieldErrors['confirm']}</p>}
            </div>

            <button type="submit" data-variant="primary" className={styles.submit} disabled={submitting}>
              {submitting ? '변경 중…' : '비밀번호 변경'}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}
